import {
  CSSProperties,
  MutableRefObject,
  Ref,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ImageMarkerValue } from './ImageMarkerBox';
import { ImageMarkerView } from './ImageMarkerView';
import styles from './ImageMarkerBox.module.scss';

const ZOOM = 2;

interface FocusedImageLayout {
  width: number;
  height: number;
  left: number;
  top: number;
}

export interface SingleImageMarkerViewProps<T extends ImageMarkerValue> {
  marker: T;
  src: string;
  readonly?: boolean;
  imageRef?: Ref<HTMLDivElement>;
  onImageClick?: () => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  (ref as MutableRefObject<T | null>).current = value;
}

function calculateFocusedImageLayout(params: {
  containerWidth: number;
  containerHeight: number;
  imageWidth: number;
  imageHeight: number;
  markerX: number;
  markerY: number;
  zoom: number;
}): FocusedImageLayout {
  const {
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
    markerX,
    markerY,
    zoom,
  } = params;

  const containScale = Math.min(
    containerWidth / imageWidth,
    containerHeight / imageHeight,
  );

  const scale = containScale * zoom;

  const renderedWidth = imageWidth * scale;
  const renderedHeight = imageHeight * scale;

  const normalizedMarkerX = clamp(markerX, 0, 100) / 100;
  const normalizedMarkerY = clamp(markerY, 0, 100) / 100;

  const markerRenderedX = renderedWidth * normalizedMarkerX;

  const markerRenderedY = renderedHeight * normalizedMarkerY;

  const desiredLeft = containerWidth / 2 - markerRenderedX;

  const desiredTop = containerHeight / 2 - markerRenderedY;

  const left =
    renderedWidth <= containerWidth
      ? (containerWidth - renderedWidth) / 2
      : clamp(desiredLeft, containerWidth - renderedWidth, 0);

  const top =
    renderedHeight <= containerHeight
      ? (containerHeight - renderedHeight) / 2
      : clamp(desiredTop, containerHeight - renderedHeight, 0);

  return {
    width: renderedWidth,
    height: renderedHeight,
    left,
    top,
  };
}

export function SingleImageMarkerView<T extends ImageMarkerValue>(
  props: SingleImageMarkerViewProps<T>,
) {
  const { marker, src, readonly = true, imageRef, onImageClick } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [layout, setLayout] = useState<FocusedImageLayout | null>(null);

  const setContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      containerRef.current = element;
      assignRef(imageRef, element);
    },
    [imageRef],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const image = new Image();

    const updateLayout = () => {
      if (
        !container.clientWidth ||
        !container.clientHeight ||
        !image.naturalWidth ||
        !image.naturalHeight
      ) {
        return;
      }

      setLayout(
        calculateFocusedImageLayout({
          containerWidth: container.clientWidth,
          containerHeight: container.clientHeight,
          imageWidth: image.naturalWidth,
          imageHeight: image.naturalHeight,
          markerX: marker.x,
          markerY: marker.y,
          zoom: ZOOM,
        }),
      );
    };

    image.onload = updateLayout;
    image.src = src;

    const resizeObserver = new ResizeObserver(() => {
      updateLayout();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      image.onload = null;
    };
  }, [src, marker.x, marker.y]);

  const canvasStyle: CSSProperties | undefined = layout
    ? {
        width: layout.width,
        height: layout.height,
        left: layout.left,
        top: layout.top,
      }
    : undefined;

  return (
    <div
      ref={setContainerRef}
      className={styles.singleView}
      onClick={onImageClick}
    >
      {layout && (
        <div className={styles.singleViewCanvas} style={canvasStyle}>
          <ImageMarkerView
            markers={[marker]}
            src={src}
            className={styles.singleInnerView}
            readonly={readonly}
          />
        </div>
      )}
    </div>
  );
}
